import { HttpError, useCustom, useTranslate } from "@refinedev/core";
import { Flex, Spin } from "antd";
import { FaceProfileResponse } from "pages/media/types";
import { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { CascadingFilters } from "./CascadingFilters";
import { CloneButton } from "./CloneButton";
import { categories } from "./helpers";
import { useInfiniteVoiceFaceProfiles } from "./hooks/useInfiniteVoiceFaceProfiles";
import { InfiniteProfileList } from "./InfiniteProfileList";
import {
  ActiveTabName,
  FaceProfileCreationResponse,
  VoiceAndFaceContainerProps,
  VoiceFilterOptions,
} from "./types";
import VoiceAndFaceSelector from "./VoiceAndFaceSelector";

const FaceSelector = ({
  selectedFace,
  cloneFaceResponse,
  setClonedFace,
  setSelectedFace,
  setCurrentModal,
  clonedFace,
  isLoadingExistingContact,
  isNewAvatar,
  style,
}: VoiceAndFaceContainerProps) => {
  const t = useTranslate();
  const [activeKey, setActiveKey] = useState(null);

  const { id: recordId } = useParams();
  const location = useLocation();
  const isContactsRoute = location.pathname.includes("/contacts/");
  // only set contactId if we are on the contacts route
  const contactId = isContactsRoute && !!recordId;

  const [initialSelectedFaceId, setInitialSelectedFaceId] = useState<
    string | undefined
  >();

  const [filters, setFilters] = useState<Record<string, string[]>>({});

  const activeKeyFromSelectedFace =
    selectedFace?.category === ActiveTabName.Custom
      ? ActiveTabName.Custom
      : ActiveTabName.Premade;

  const currentActiveKey = activeKey ?? activeKeyFromSelectedFace;

  const { data: filterOptions, isLoading: isLoadingFilterOptions } = useCustom<
    { key: string; values: string[] }[],
    HttpError,
    unknown,
    unknown,
    VoiceFilterOptions
  >({
    url: "media/face_profiles/filters",
    config: {
      query: {
        category: "Premade",
      },
    },
    method: "get",
    queryOptions: {
      enabled: currentActiveKey === ActiveTabName.Premade,
      select: (data) => {
        return {
          total: data.data.length,
          data: data.data.reduce((acc, { key, values }) => {
            acc[key] = values;
            return acc;
          }, {} as VoiceFilterOptions),
        };
      },
    },
  });

  useEffect(() => {
    if (
      (!initialSelectedFaceId && selectedFace?.id) ||
      (cloneFaceResponse && selectedFace?.id)
    ) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setInitialSelectedFaceId(selectedFace.id);
    }
  }, [selectedFace?.id, cloneFaceResponse, initialSelectedFaceId]);

  const previewProfile = clonedFace || cloneFaceResponse || selectedFace;
  const profileCategory = previewProfile?.category;
  const selectedProfileHasSameCategory =
    (profileCategory && categories[profileCategory]) === currentActiveKey ||
    !!clonedFace;

  // Insert the selected profile at the beginning of the list if:
  // 1. The selected profile has the same category as the current tab;
  // 2. There are no filters applied or the tab is "Custom" (2nd).
  const insertSelected =
    !!cloneFaceResponse || (contactId && selectedProfileHasSameCategory);

  const shouldInsertSelected =
    !!cloneFaceResponse || initialSelectedFaceId === selectedFace?.id;

  const { profiles, isLoading, hasMore, loadMoreData } =
    useInfiniteVoiceFaceProfiles<Partial<FaceProfileResponse>>({
      type: "face",
      resourceType: currentActiveKey,
      previewProfile:
        previewProfile ?? (cloneFaceResponse ? selectedFace : clonedFace),
      pageSize: 10,
      filters: currentActiveKey === ActiveTabName.Premade ? filters : undefined,
      insertSelected:
        shouldInsertSelected &&
        insertSelected &&
        Object.keys(filters).length === 0,
    });

  const handleClearAllFilters = () => {
    setFilters({});
  };

  const handleFiltersChange = (key: string, value: string) => {
    setFilters((prev) => {
      const currentValues = prev[key] ?? [];
      const exists = currentValues.includes(value);

      const updatedValues = exists
        ? currentValues.filter((v) => v !== value)
        : [...currentValues, value];

      const newFilters = { ...prev };
      if (updatedValues.length > 0) {
        newFilters[key] = updatedValues;
      } else {
        delete newFilters[key];
      }
      return newFilters;
    });
  };

  return (
    <Flex vertical style={{ ...style, height: "100%" }}>
      <Flex vertical style={{ height: "100%" }} gap={16}>
        {isLoadingExistingContact && !!recordId ? (
          <Flex
            style={{ width: "100%", height: "100%" }}
            justify="center"
            align="center"
          >
            <Spin size="large" />
          </Flex>
        ) : (
          <VoiceAndFaceSelector
            type="face"
            onTabClick={(key) => {
              setActiveKey(key);
            }}
            activeKey={currentActiveKey}
            filters={
              isLoadingFilterOptions ? (
                <Spin spinning={true} />
              ) : (
                <CascadingFilters
                  type="face"
                  onChange={handleFiltersChange}
                  onClearAll={handleClearAllFilters}
                  filters={filters}
                  options={filterOptions?.data}
                  itemsCount={profiles?.length ?? 0}
                  label={t(
                    "components.ContactWithPresenter.VoiceFilter.filters"
                  )}
                  loading={isLoadingFilterOptions}
                />
              )
            }
            premade={
              <InfiniteProfileList<
                FaceProfileResponse,
                FaceProfileCreationResponse
              >
                type="face"
                profiles={profiles}
                isLoading={isLoading}
                selected={selectedFace}
                clonedResponse={cloneFaceResponse}
                setCloned={setClonedFace}
                setSelected={setSelectedFace}
                hasMore={hasMore}
                loadMoreData={loadMoreData}
                isNewAvatar={isNewAvatar}
              />
            }
            custom={
              <InfiniteProfileList
                type="face"
                profiles={profiles}
                isLoading={isLoading}
                selected={selectedFace}
                clonedResponse={cloneFaceResponse}
                setCloned={setClonedFace}
                setSelected={setSelectedFace}
                hasMore={hasMore}
                loadMoreData={loadMoreData}
                cloneButton={
                  profiles.length === 0 && !isLoading ? (
                    <CloneButton
                      type="primary"
                      title={t(
                        "components.ContactWithPresenter.FaceSelector.cloneANew"
                      )}
                      onClick={() => {
                        setCurrentModal("face");
                      }}
                    />
                  ) : undefined
                }
                isNewAvatar={isNewAvatar}
              />
            }
          />
        )}
        {currentActiveKey === ActiveTabName.Custom &&
          profiles.length > 0 &&
          !isLoading && (
            <CloneButton
              title={t(
                "components.ContactWithPresenter.FaceSelector.cloneANew"
              )}
              onClick={() => {
                setCurrentModal("face");
              }}
            />
          )}
      </Flex>
    </Flex>
  );
};

export default FaceSelector;
